import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Flag, Lock, Unlock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import type { ShippingRule, ShippingRuleCondition } from '@/types';
import { formatRuleSummary } from '@/utils/formatRuleSummary';

interface ShippingRuleCardProps {
  rule: ShippingRule;
  onToggle: () => void;
  onDelete: () => void;
  onEdit?: () => void;
  // Item 1 — Lock Shipping Rules. When true the card is view-only
  // regardless of rule_origin.
  locked?: boolean;
}

function pickConditionForColumn(
  conditions: ShippingRuleCondition[] | undefined,
  field: 'source' | 'destination',
): string {
  const hit = conditions?.find((c) => c.field === field);
  if (!hit) return '—';
  const v = hit.value;
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

export function ShippingRuleCard({
  rule,
  onToggle,
  onDelete,
  onEdit,
  locked = false,
}: ShippingRuleCardProps) {
  const { t } = useTranslation();
  const isAgent = rule.rule_origin === 'agent';
  const readOnly = isAgent || locked;
  const carriers = rule.carriers ?? [];
  const summary = formatRuleSummary(rule.conditions ?? [], t as any);

  return (
    <div className='rounded-xl border border-sd-border bg-sd-card p-4 transition-shadow hover:shadow-md'>
      {/* Header: Name + Toggle */}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-1.5 min-w-0 flex-wrap'>
          <span className='text-sm font-semibold truncate'>{rule.name}</span>
          {rule.filter_by_criteria &&
            (() => {
              const key =
                rule.filter_by_criteria === 'cheapest'
                  ? 'filter_cheapest'
                  : rule.filter_by_criteria === 'most_reliable'
                    ? 'filter_most_reliable'
                    : 'filter_fastest';
              const tone =
                rule.filter_by_criteria === 'cheapest'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : rule.filter_by_criteria === 'fastest'
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-violet-50 text-violet-700 border-violet-200';
              return (
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
                >
                  {String(t(key))}
                </span>
              );
            })()}
          {/* SR-OVR — overridable / enforced status, shown on the mobile card
              too so the at-a-glance signal is consistent with the table. */}
          {(() => {
            const isOverridable = rule.is_overridable === true;
            const tone = isOverridable
              ? 'bg-sky-50 text-sky-700 border-sky-200'
              : 'bg-slate-50 text-slate-600 border-slate-200';
            const OvrIcon = isOverridable ? Unlock : Lock;
            return (
              <span
                data-testid={`rule-overridable-${isOverridable ? 'yes' : 'no'}`}
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
              >
                <OvrIcon className='h-3 w-3 shrink-0' aria-hidden='true' />
                {String(
                  t(
                    isOverridable
                      ? 'shipping_rules.overridable_badge_yes'
                      : 'shipping_rules.overridable_badge_no',
                  ),
                )}
              </span>
            );
          })()}
          {readOnly && <Lock className='h-3.5 w-3.5 shrink-0 text-sd-muted-foreground' />}
        </div>
        <Switch
          checked={rule.is_active}
          onCheckedChange={onToggle}
          disabled={readOnly}
          className='data-[state=checked]:bg-sd-primary'
        />
      </div>

      {/* Carriers */}
      <div className='flex flex-wrap gap-1.5 mb-3'>
        {carriers.map((c) => (
          <span
            key={c.uid}
            className='rounded-md bg-sd-carrier-badge px-2.5 py-0.5 text-xs font-medium'
          >
            {c.title}
          </span>
        ))}
      </div>

      {/* Destination — Item 3: the list shows the destination only; the
          source condition still exists on the rule but is not surfaced. */}
      <div className='flex gap-4 mb-3'>
        <span className='flex items-center gap-2 text-sm'>
          <Flag className='h-3.5 w-3.5 shrink-0 text-sd-dest-green fill-sd-dest-green' />
          {pickConditionForColumn(rule.conditions, 'destination')}
        </span>
      </div>

      {/* Description */}
      <div className='rounded-lg px-3 py-2 text-xs leading-relaxed mb-3 bg-[hsl(var(--sd-desc-bg-blue))] text-[hsl(var(--sd-desc-text-blue))]'>
        {summary}
      </div>

      {/* Actions always render. Disabled per-row for agent rules (RS-32) and
          for every row when the account is locked (Item 1). */}
      <div className='flex gap-2 justify-end'>
        <button
          onClick={onEdit}
          disabled={readOnly || !onEdit}
          className='rounded-lg border border-sd-border p-2 transition-colors hover:bg-sd-muted disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <Pencil className='h-4 w-4 text-sd-muted-foreground' />
        </button>
        <button
          onClick={onDelete}
          disabled={readOnly}
          className='rounded-lg border border-sd-border p-2 transition-colors hover:bg-sd-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <Trash2 className='h-4 w-4 text-sd-muted-foreground' />
        </button>
      </div>
    </div>
  );
}
